import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  UploadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import {
  Button,
  Form,
  message,
  Modal,
  Radio,
  Switch,
  Timeline,
  Tooltip,
  Upload,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import RichEditor from '../../../../components/RichEditor/RichEditor'
import utils from '../../../../utils'
import { renderElementToPdfFile } from '../../../../utils/pdfcanvasutils '
import ExportPdf from '../ExportPdf/ExportPdf'
import { apiUrl } from '../../../../config'
import FileItem from '../../../../components/Common/FileItemComponent/FileItem'
import UploadFileItem from '../../../../components/Common/FileItemComponent/UploadFileItem'

const styles = {
  votingSection: {
    marginBottom: '20px',
    padding: '16px',
    background: '#f9f9fb',
    borderRadius: '8px',
    border: '1px solid #e8e8f0',
  },
  votingLabel: {
    fontWeight: 600,
    marginBottom: '10px',
    fontSize: '14px',
    color: '#333',
  },
  radioItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  disagreeHint: {
    color: '#ff4d4f',
    fontSize: '12px',
    fontStyle: 'italic',
  },
  commentListWrapper: {
    maxHeight: '380px',
    overflow: 'auto',
    marginBottom: '16px',
    paddingRight: '8px',
  },
  commentListTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  commentListTitle: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#444',
    borderLeft: '3px solid #1890ff',
    paddingLeft: '8px',
  },
  showDeletedToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#666',
  },
  emptyComment: {
    color: '#aaa',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '24px 0',
    fontSize: '13px',
  },
  commentItem: {
    marginBottom: '12px',
    padding: '10px 14px',
    borderRadius: '6px',
    background: '#fff',
    border: '1px solid #eef0f5',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  commentItemDeleted: {
    background: '#fff1f0',
    border: '1px solid #ffccc7',
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  commentAuthor: {
    fontWeight: 600,
    fontSize: '13px',
    color: '#333',
  },
  deletedTag: {
    color: '#ff4d4f',
    fontWeight: 400,
    fontSize: '11px',
    marginLeft: '6px',
  },
  commentMeta: {
    color: '#aaa',
    fontSize: '11px',
  },
  commentActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  actionBtn: {
    padding: 0,
    height: 'auto',
    fontSize: '13px',
  },
  commentBody: {
    margin: 0,
    color: '#444',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  commentBodyDeleted: {
    color: '#bfbfbf',
    textDecoration: 'line-through',
  },
  editedHint: {
    color: '#8c8c8c',
    fontSize: '11px',
    fontStyle: 'italic',
    marginTop: '4px',
  },
  formSection: {
    borderTop: '1px solid #eef0f5',
    paddingTop: '16px',
  },
  votingCompletedHint: {
    background: '#fffbe6',
    border: '1px solid #ffe58f',
    borderRadius: '6px',
    padding: '8px 12px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#874d00',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '12px',
  },
  historyContent: {
    maxHeight: '450px',
    overflow: 'auto',
  },
  historyBlock: {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: '4px',
    padding: '8px 10px',
    marginTop: '6px',
    fontSize: '12px',
  },
}

const stripHtml = html =>
  (html || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()

const FeedbackVotingPopup = ({
  visible,
  onClose,
  authenticationStore,
  FeedbackCommentStore,
  fetchFeedbackDetailData,
  feedbackDetailData,
  fileStore,
  fetchDetailComment,
  userList,
  userNameHaventVoting,
  votingCommentId,
}) => {
  const { feedbackCommentDataDetailV2 } = FeedbackCommentStore
  const [form] = Form.useForm()
  const EDITOR_REF = useRef(null)
  const [selectedOption, setSelectedOption] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentComments, setCurrentComments] = useState([])
  const [editorContent, setEditorContent] = useState('')
  const [editingComment, setEditingComment] = useState(null)
  const [editorVersion, setEditorVersion] = useState(0)
  const [attachedFiles, setAttachedFiles] = useState([])
  const [historyVisible, setHistoryVisible] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyItems, setHistoryItems] = useState([])
  const [historyTitle, setHistoryTitle] = useState('Lịch sử chỉnh sửa')
  const [showDeleted, setShowDeleted] = useState(false)
  const { currentUser } = authenticationStore

  const latestFeedbackForCurrentUser = feedbackCommentDataDetailV2?.feedbackCommentDetails
    ?.filter(
      item =>
        item.feedbackUsername === currentUser.username &&
        item.voteType &&
        !item.isDeleted
    )
    ?.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0]

  const isVotingCompleted = useMemo(() => {
    const picUsers = userNameHaventVoting || []
    if (!picUsers.length) return false
    const votedUsers = new Set(
      (feedbackCommentDataDetailV2?.feedbackCommentDetails || [])
        .filter(d => !d.isDeleted && d.voteType)
        .map(d => d.feedbackUsername)
    )
    return picUsers.every(username => votedUsers.has(username))
  }, [feedbackCommentDataDetailV2, userNameHaventVoting])

  const canModifyOwnComment = comment =>
    !!currentUser?.username &&
    comment?.feedbackUsername === currentUser.username &&
    !comment?.isDeleted &&
    !isVotingCompleted

  useEffect(() => {
    if (latestFeedbackForCurrentUser) {
      setSelectedOption(latestFeedbackForCurrentUser.voteType)
      form.setFieldsValue({
        voteType: latestFeedbackForCurrentUser.voteType || null,
      })
    } else {
      setSelectedOption(null)
      form.setFieldsValue({ voteType: null })
    }
  }, [latestFeedbackForCurrentUser, form])

  useEffect(() => {
    if (!visible) {
      setEditingComment(null)
      setEditorContent('')
    }
  }, [visible])

  const getMergedComments = ({
    newComment = '',
    overrideId = null,
    overrideText = null,
    excludeId = null,
  } = {}) => {
    const existing =
      feedbackCommentDataDetailV2?.feedbackCommentDetails
        ?.filter(
          c =>
            !c.isDeleted &&
            c.id !== excludeId &&
            c.detailFeedbackComment &&
            stripHtml(c.detailFeedbackComment) !== '' &&
            c.feedbackUsername === currentUser.username
        )
        .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
        .map(c =>
          overrideId && c.id === overrideId
            ? overrideText
            : c.detailFeedbackComment
        ) || []

    if (newComment && stripHtml(newComment)) {
      return [...existing, newComment]
    }
    return existing.filter(Boolean)
  }

  // Xuất PDF theo mẫu ExportPdf cũ (html2canvas — giữ đúng layout 2 cột)
  const generateAndUploadPdf = async commentsForPdf => {
    setCurrentComments(commentsForPdf)
    await new Promise(resolve => setTimeout(resolve, 300))

    const printEl = document.getElementById('divToPrint')
    if (!printEl) {
      throw new Error('Không tìm thấy nội dung PDF để xuất')
    }

    const now = new Date()
    const formattedDateTime = `${now.getDate().toString().padStart(2, '0')}_${(
      now.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}_${now.getFullYear()}_${now
      .getHours()
      .toString()
      .padStart(2, '0')}_${now.getMinutes().toString().padStart(2, '0')}`

    const pdfFile = await renderElementToPdfFile(
      'divToPrint',
      `${currentUser?.name_uppercase}_${formattedDateTime}`
    )
    if (!pdfFile) {
      throw new Error('Xuất PDF thất bại')
    }
    const formData = new FormData()
    formData.append('file', pdfFile)
    const responseSignDoc = await fileStore.uploadFile(formData)
    return {
      file_id: responseSignDoc.data.file_id,
      file_name: responseSignDoc.data.file_name,
    }
  }

  const refreshData = async () => {
    await Promise.all([
      fetchFeedbackDetailData(),
      fetchDetailComment(votingCommentId),
    ])
  }

  const clearEditor = () => {
    setEditingComment(null)
    setEditorContent('')
    setAttachedFiles([])
    setEditorVersion(v => v + 1)
    try {
      if (EDITOR_REF.current?.editor) {
        EDITOR_REF.current.editor.setData('')
      }
    } catch (e) {
      // ignore
    }
  }

  const handleSubmit = async () => {
    const editorComment = EDITOR_REF.current?.editor?.getData() || ''

    if (editingComment) {
      if (!stripHtml(editorComment)) {
        message.error('Vui lòng nhập nội dung ý kiến!')
        return
      }
      if (!canModifyOwnComment(editingComment)) {
        message.error(
          'Bạn không thể chỉnh sửa ý kiến này (chỉ người tạo, trước khi HĐQT hoàn thành biểu quyết).'
        )
        return
      }

      setIsSubmitting(true)
      try {
        const attachedFilesJSON = attachedFiles.length > 0
          ? JSON.stringify(attachedFiles.map(f => ({ fileId: f.file_id || f.response?.file_id, fileName: f.file_name || f.response?.file_name })))
          : null;

        const merged = getMergedComments({
          overrideId: editingComment.id,
          overrideText: editorComment,
        })
        const hasVote =
          !!selectedOption || !!latestFeedbackForCurrentUser?.voteType
        let pdfResult = null
        if (hasVote) {
          pdfResult = await generateAndUploadPdf(merged)
        }

        await FeedbackCommentStore.updateCommentDetail(editingComment.id, {
          feedbackUsername: currentUser?.username,
          detailFeedbackComment: editorComment,
          voteType: editingComment.voteType || undefined,
          attachedFiles: attachedFilesJSON,
          ...(pdfResult
            ? { fileId: pdfResult.file_id, fileName: pdfResult.file_name }
            : {}),
        })

        if (pdfResult) {
          const latestVoteRecord = feedbackCommentDataDetailV2?.feedbackCommentDetails
            ?.filter(
              item =>
                item.feedbackUsername === currentUser.username &&
                item.voteType &&
                !item.isDeleted &&
                item.id !== editingComment.id
            )
            ?.sort(
              (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
            )[0]

          if (latestVoteRecord?.id) {
            await FeedbackCommentStore.updateVotingStatus(latestVoteRecord.id, {
              fileId: pdfResult.file_id,
              fileName: pdfResult.file_name,
            })
          }
        }

        message.success('Cập nhật ý kiến thành công!')
        clearEditor()
        await refreshData()
      } catch (err) {
        console.error(err)
        message.error('Có lỗi xảy ra khi cập nhật ý kiến!')
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    if (!selectedOption && !stripHtml(editorComment)) {
      message.error(
        'Vui lòng chọn Tán thành/Không tán thành hoặc nhập nội dung ý kiến!'
      )
      return
    }

    setIsSubmitting(true)
    try {
      const attachedFilesJSON = attachedFiles.length > 0
          ? JSON.stringify(attachedFiles.map(f => ({ fileId: f.file_id || f.response?.file_id, fileName: f.file_name || f.response?.file_name })))
          : null;

      const merged = getMergedComments({ newComment: editorComment })
      const dataSubmit = {
        feedbackUsername: currentUser?.username,
        detailFeedbackComment: editorComment,
        voteType: selectedOption || undefined,
        attachedFiles: attachedFilesJSON,
      }

      // Chỉ tạo/hiển thị PDF khi đã chọn phương án biểu quyết
      if (selectedOption) {
        const pdfResult = await generateAndUploadPdf(merged)
        dataSubmit.fileId = pdfResult.file_id
        dataSubmit.fileName = pdfResult.file_name
      }

      if (latestFeedbackForCurrentUser) {
        await FeedbackCommentStore.updateCommentDetail(
          latestFeedbackForCurrentUser.id,
          dataSubmit
        )
      } else {
        await FeedbackCommentStore.updateComment(dataSubmit, votingCommentId)
      }

      message.success(
        selectedOption ? 'Biểu quyết thành công!' : 'Đã thêm ý kiến!'
      )
      clearEditor()
      await refreshData()

      if (
        selectedOption &&
        userNameHaventVoting.includes(currentUser.username)
      ) {
        onClose()
      }
    } catch (err) {
      console.error(err)
      message.error('Có lỗi xảy ra khi lưu!')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditComment = comment => {
    if (!canModifyOwnComment(comment)) {
      message.warning(
        isVotingCompleted
          ? 'Không thể chỉnh sửa sau khi các thành viên HĐQT đã hoàn thành biểu quyết.'
          : 'Bạn chỉ có thể chỉnh sửa ý kiến của mình.'
      )
      return
    }
    setEditingComment(comment)
    setEditorContent(comment.detailFeedbackComment || '')
    let parsedFiles = []
    try {
      if (comment.attachedFiles) {
         parsedFiles = JSON.parse(comment.attachedFiles)
      }
    } catch (e) {
      console.error(e)
    }
    setAttachedFiles(parsedFiles.map((f, i) => ({
       uid: f.fileId || `old-${i}`,
       name: f.fileName,
       status: 'done',
       file_id: f.fileId,
       file_name: f.fileName,
       url: `${apiUrl}/api/v1/upload/attachments/${f.fileId}`
    })))
    setEditorVersion(v => v + 1)
  }

  const handleCancelEdit = () => {
    clearEditor()
  }

  const handleDeleteComment = comment => {
    if (!canModifyOwnComment(comment)) {
      message.warning(
        isVotingCompleted
          ? 'Không thể xóa sau khi các thành viên HĐQT đã hoàn thành biểu quyết.'
          : 'Bạn chỉ có thể xóa ý kiến của mình.'
      )
      return
    }

    Modal.confirm({
      title: 'Xóa ý kiến',
      content:
        'Bạn có chắc muốn xóa ý kiến này? Thao tác sẽ được lưu vào lịch sử.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        setIsSubmitting(true)
        try {
          await FeedbackCommentStore.deleteCommentDetail(comment.id)

          const merged = getMergedComments({ excludeId: comment.id })
          const hasVote =
            !!selectedOption || !!latestFeedbackForCurrentUser?.voteType
          if (
            hasVote &&
            (merged.length > 0 ||
              selectedOption ||
              latestFeedbackForCurrentUser)
          ) {
            const pdfResult = await generateAndUploadPdf(merged)
            const latestVoteRecord = feedbackCommentDataDetailV2?.feedbackCommentDetails
              ?.filter(
                item =>
                  item.feedbackUsername === currentUser.username &&
                  item.voteType &&
                  !item.isDeleted &&
                  item.id !== comment.id
              )
              ?.sort(
                (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
              )[0]

            if (latestVoteRecord?.id) {
              await FeedbackCommentStore.updateVotingStatus(
                latestVoteRecord.id,
                {
                  fileId: pdfResult.file_id,
                  fileName: pdfResult.file_name,
                }
              )
            }
          }

          if (editingComment?.id === comment.id) {
            clearEditor()
          }

          message.success('Đã xóa ý kiến thành công!')
          await refreshData()
        } catch (err) {
          console.error(err)
          message.error('Có lỗi xảy ra khi xóa ý kiến!')
        } finally {
          setIsSubmitting(false)
        }
      },
    })
  }

  const handleShowHistory = async comment => {
    setHistoryTitle(
      `Lịch sử chỉnh sửa — ${getNameUppercaseByUsername(
        comment.feedbackUsername
      )}`
    )
    setHistoryVisible(true)
    setHistoryLoading(true)
    setHistoryItems([])
    try {
      const response = await FeedbackCommentStore.getCommentDetailHistory(
        comment.id
      )
      setHistoryItems(response?.data || [])
    } catch (err) {
      console.error(err)
      message.error('Không tải được lịch sử chỉnh sửa!')
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleCancel = () => {
    clearEditor()
    onClose()
  }

  function getNameUppercaseByUsername(username) {
    const user = userList?.find(user => user.username === username)
    return user ? utils.getNameInCapitalize(user.name_uppercase) : 'Admin'
  }

  const formatDate = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}/${month}/${date.getFullYear()}`
  }

  const formatTime = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`
  }

  const formatDateTime = dateString =>
    `${formatDate(dateString)} ${formatTime(dateString)}`

  const allCommentsWithContent = feedbackCommentDataDetailV2?.feedbackCommentDetails?.filter(
    d => d.detailFeedbackComment
  )

  const deletedCommentsCount =
    allCommentsWithContent?.filter(d => d.isDeleted)?.length || 0

  const commentsWithContent = showDeleted
    ? allCommentsWithContent
    : allCommentsWithContent?.filter(d => !d.isDeleted)

  const getVoteLabel = voteType => {
    switch (voteType) {
      case 'AGREE':
        return 'Tán thành'
      case 'DISAGREE':
        return 'Không tán thành'
      case 'OTHER':
        return 'Không có ý kiến'
      default:
        return voteType || ''
    }
  }

  const isEdited = comment => {
    if (!comment.updatedAt || !comment.createdAt) return false
    const diff = Math.abs(
      Date.parse(comment.updatedAt) - Date.parse(comment.createdAt)
    )
    return diff > 1000
  }

  // Enrich feedbackComments của nội dung đang vote bằng detail mới nhất
  const enrichedFeedbackDetailData = useMemo(() => {
    if (!feedbackDetailData) return feedbackDetailData
    const details = feedbackCommentDataDetailV2?.feedbackCommentDetails || []
    return {
      ...feedbackDetailData,
      feedbackComments: (feedbackDetailData.feedbackComments || []).map(
        comment =>
          comment.id === votingCommentId
            ? { ...comment, feedbackCommentDetails: details }
            : comment
      ),
    }
  }, [feedbackDetailData, feedbackCommentDataDetailV2, votingCommentId])

  return (
    <>
      <Modal
        title="Ý kiến biểu quyết"
        visible={visible}
        width={800}
        onCancel={handleCancel}
        footer={null}
        bodyStyle={{ padding: '20px 24px' }}>
        {userNameHaventVoting.includes(currentUser.username) &&
          !editingComment && (
            <div style={styles.votingSection}>
              <div style={styles.votingLabel}>Nhập lựa chọn:</div>
              <Radio.Group
                onChange={e => setSelectedOption(e.target.value)}
                value={selectedOption}
                disabled={isVotingCompleted}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}>
                  <Radio value="AGREE">Tán thành</Radio>
                  <div style={styles.radioItem}>
                    <Radio value="DISAGREE">Không tán thành</Radio>
                    {selectedOption === 'DISAGREE' && (
                      <span style={styles.disagreeHint}>
                        (Vui lòng nêu rõ lý do không tán thành)
                      </span>
                    )}
                  </div>
                  <Radio value="OTHER">Không có ý kiến</Radio>
                </div>
              </Radio.Group>
            </div>
          )}
        {isVotingCompleted && !editingComment && (
          <div style={styles.votingCompletedHint}>
            Tất cả thành viên đã hoàn thành biểu quyết. Không thể sửa/xóa các ý
            kiến đã đăng trước đó.
          </div>
        )}
        <div style={styles.commentListWrapper}>
          <div style={styles.commentListTitleRow}>
            <div style={styles.commentListTitle}>Danh sách ý kiến:</div>
            {deletedCommentsCount > 0 && (
              <div style={styles.showDeletedToggle}>
                <span>Hiển thị ý kiến đã xóa ({deletedCommentsCount})</span>
                <Switch
                  size="small"
                  checked={showDeleted}
                  onChange={setShowDeleted}
                />
              </div>
            )}
          </div>

          {commentsWithContent?.length === 0 && (
            <div style={styles.emptyComment}>Chưa có ý kiến nào.</div>
          )}

          {commentsWithContent?.map(comment => (
            <div
              key={comment.id}
              style={{
                ...styles.commentItem,
                ...(comment.isDeleted ? styles.commentItemDeleted : {}),
              }}>
              <div style={styles.commentHeader}>
                <span style={styles.commentAuthor}>
                  {getNameUppercaseByUsername(comment.feedbackUsername)}
                  {comment.isDeleted && (
                    <span style={styles.deletedTag}>(Đã xóa)</span>
                  )}
                </span>
                <div style={styles.commentActions}>
                  <span style={styles.commentMeta}>
                    {formatDate(comment.createdAt)}{' '}
                    {formatTime(comment.createdAt)}
                    {isEdited(comment) ? ' (đã sửa)' : ''}
                  </span>
                  <Tooltip title="Lịch sử chỉnh sửa">
                    <Button
                      type="link"
                      style={styles.actionBtn}
                      icon={<HistoryOutlined />}
                      onClick={() => handleShowHistory(comment)}
                    />
                  </Tooltip>
                  {canModifyOwnComment(comment) && (
                    <>
                      <Tooltip title="Sửa ý kiến">
                        <Button
                          type="link"
                          style={styles.actionBtn}
                          icon={<EditOutlined />}
                          onClick={() => handleEditComment(comment)}
                        />
                      </Tooltip>
                      <Tooltip title="Xóa ý kiến">
                        <Button
                          type="link"
                          danger
                          style={styles.actionBtn}
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteComment(comment)}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
              <div
                style={{
                  ...styles.commentBody,
                  ...(comment.isDeleted ? styles.commentBodyDeleted : {}),
                }}
                dangerouslySetInnerHTML={{
                  __html: comment.detailFeedbackComment,
                }}
              />
              {comment.attachedFiles && !comment.isDeleted && (
                <div style={{ marginTop: '8px' }}>
                  {(() => {
                    try {
                      const files = JSON.parse(comment.attachedFiles)
                      return files.map((f, i) => (
                        <FileItem
                          key={i}
                          file_id={f.fileId}
                          file_name={f.fileName}
                          file_type={f.fileName ? f.fileName.split('.').pop().toLowerCase() : ''}
                        />
                      ))
                    } catch (e) {
                      return null
                    }
                  })()}
                </div>
              )}
              {isEdited(comment) && !comment.isDeleted && (
                <div style={styles.editedHint}>
                  Cập nhật lần cuối: {formatDateTime(comment.updatedAt)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={styles.formSection}>
          <Form form={form} layout="vertical">
            <Form.Item
              label={
                editingComment ? 'Chỉnh sửa nội dung ý kiến' : 'Nội dung ý kiến'
              }
              style={{ marginBottom: '12px' }}>
              <RichEditor
                key={
                  editingComment
                    ? `edit-${editingComment.id}-${editorVersion}`
                    : `create-${editorVersion}`
                }
                EDITOR_REF={EDITOR_REF}
                editorContent={editorContent || ''}
                height={100}
                onEditorChange={content =>
                  form.setFieldsValue({ comment: content })
                }
              />
            </Form.Item>

            {attachedFiles && attachedFiles.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                {attachedFiles.map(file => (
                  <UploadFileItem
                    key={file.uid}
                    file_id={file.file_id}
                    file_name={file.name || file.file_name}
                    file_type={file.name ? file.name.split('.').pop().toLowerCase() : (file.file_name ? file.file_name.split('.').pop().toLowerCase() : '')}
                    handleRemoveFileFromUploadList={() => {
                      setAttachedFiles(prev => prev.filter(f => f.uid !== file.uid))
                    }}
                  />
                ))}
              </div>
            )}

            <div style={styles.footer}>
              <div style={{ flex: 1 }}>
                <Upload
                  multiple
                  showUploadList={false}
                  fileList={attachedFiles}
                  customRequest={async options => {
                    const { file, onSuccess, onError } = options
                    const formData = new FormData()
                    formData.append('file', file)
                    try {
                      const res = await fileStore.uploadFile(formData)
                      if (res && res.data) {
                        file.file_id = res.data.file_id
                        file.file_name = res.data.file_name
                        file.url = `${apiUrl}/api/v1/upload/attachments/${res.data.file_id}`
                        onSuccess(res.data, file)
                      }
                    } catch (e) {
                      console.error(e)
                      onError(e)
                      message.error('Upload thất bại')
                    }
                  }}
                  onChange={info => {
                    let newFileList = [...info.fileList];
                    newFileList = newFileList.map(file => {
                      if (file.response) {
                        file.file_id = file.response.file_id;
                        file.file_name = file.response.file_name;
                        file.url = `${apiUrl}/api/v1/upload/attachments/${file.response.file_id}`;
                      }
                      return file;
                    });
                    setAttachedFiles(newFileList);
                  }}
                >
                  <Button icon={<PaperClipOutlined />}>Tải lên file đính kèm</Button>
                </Upload>
              </div>
              {editingComment && (
                <Button onClick={handleCancelEdit} disabled={isSubmitting}>
                  Hủy sửa
                </Button>
              )}
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={isSubmitting}>
                {editingComment
                  ? 'Cập nhật ý kiến'
                  : selectedOption
                  ? 'Lưu biểu quyết'
                  : 'Lưu ý kiến'}
              </Button>
              <Button onClick={handleCancel}>Đóng</Button>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        title={historyTitle}
        visible={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryVisible(false)}>
            Đóng
          </Button>,
        ]}
        width={640}>
        <div style={styles.historyContent}>
          {historyLoading && <div>Đang tải lịch sử...</div>}
          {!historyLoading && historyItems.length === 0 && (
            <div style={styles.emptyComment}>Chưa có lịch sử chỉnh sửa.</div>
          )}
          {!historyLoading && historyItems.length > 0 && (
            <Timeline style={{ margin: '20px 0 0' }}>
              {historyItems.map(item => (
                <Timeline.Item
                  key={item.id}
                  color={item.action === 'DELETE' ? 'red' : 'blue'}>
                  <div>
                    <strong>
                      {item.action === 'DELETE' ? 'Xóa' : 'Chỉnh sửa'}
                    </strong>{' '}
                    bởi {getNameUppercaseByUsername(item.editedBy)} —{' '}
                    {formatDateTime(item.createdAt)}
                  </div>
                  {(item.voteTypeBefore || item.voteTypeAfter) && (
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Biểu quyết: {getVoteLabel(item.voteTypeBefore) || '—'} →{' '}
                      {getVoteLabel(item.voteTypeAfter) || '—'}
                    </div>
                  )}
                  {item.detailFeedbackCommentBefore && (
                    <div style={styles.historyBlock}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        Trước:
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: item.detailFeedbackCommentBefore,
                        }}
                      />
                    </div>
                  )}
                  {item.detailFeedbackCommentAfter && (
                    <div style={styles.historyBlock}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        Sau:
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: item.detailFeedbackCommentAfter,
                        }}
                      />
                    </div>
                  )}
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </div>
      </Modal>

      <ExportPdf
        feedbackDetailData={enrichedFeedbackDetailData}
        selectedIndex={votingCommentId}
        selectedOption={selectedOption}
        comment={currentComments?.filter(Boolean).join('<br/>') || ''}
      />
    </>
  )
}

export default inject(
  'authenticationStore',
  'FeedbackCommentStore',
  'fileStore'
)(observer(FeedbackVotingPopup))
